import { useMemo, useState } from "react";
import { Boxes } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierPickupBundleDiscountTier,
  useCarrierDiscounts,
  useUpdateCarrierPickupBundleDiscount,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BenefitRow } from "./benefit-row";
import { mutationFailure, useSubmissionGuard } from "./mutation-guards";

type PickupBundleCurve = Array<{ position: number; fractionOff: number }>;

const SERVER_FALLBACK_PICKUP_CURVE: PickupBundleCurve = [
  { position: 0, fractionOff: 0 },
  { position: 1, fractionOff: 0.5 },
  { position: 2, fractionOff: 0.75 },
];

function resolvePickupBundleCurve(
  rows: CarrierPickupBundleDiscountTier[],
  tierId: string | null
): { curve: PickupBundleCurve; source: string } {
  const exact = rows
    .filter(
      (row) =>
        row.customerId == null && (row.carrierCustomerTierId ?? null) === tierId
    )
    .sort((a, b) => a.position - b.position);
  if (exact.length > 0) {
    return {
      curve: exact,
      source: tierId ? "Tier-specific curve" : "Carrier default curve",
    };
  }

  const carrierDefault = rows
    .filter(
      (row) => row.customerId == null && row.carrierCustomerTierId == null
    )
    .sort((a, b) => a.position - b.position);
  if (carrierDefault.length > 0) {
    return { curve: carrierDefault, source: "Inherited from carrier default" };
  }

  return {
    curve: SERVER_FALLBACK_PICKUP_CURVE,
    source: tierId ? "Inherited server fallback" : "Server fallback curve",
  };
}

function curvePercentageAt(curve: PickupBundleCurve, position: number): number {
  let selected: PickupBundleCurve[number] | undefined;
  for (const row of curve) {
    if (row.position <= position) selected = row;
    else break;
  }
  return Math.round((selected?.fractionOff ?? 0) * 100);
}

export function PickupBundleBenefit({
  tierId,
  isOwner,
}: {
  tierId: string | null;
  isOwner: boolean;
}) {
  const discounts = useCarrierDiscounts();
  const resolved = useMemo(
    () =>
      resolvePickupBundleCurve(
        discounts.data?.pickupBundleDiscounts ?? [],
        tierId
      ),
    [discounts.data, tierId]
  );
  const value = discounts.isLoading
    ? "Loading..."
    : discounts.isError
      ? "Unavailable"
      : `${curvePercentageAt(resolved.curve, 1)}% / ${curvePercentageAt(resolved.curve, 2)}%`;

  return (
    <BenefitRow
      icon={<Boxes className="h-4 w-4" />}
      title="Pickup bundle"
      description={`2nd / 3rd+ package discount. ${resolved.source}.`}
      value={value}
      action={
        isOwner ? (
          <PickupBundleEditor
            tierId={tierId}
            effectiveCurve={resolved.curve}
            source={resolved.source}
            isLoading={discounts.isLoading}
            isError={discounts.isError}
            onRetry={() => void discounts.refetch()}
          />
        ) : undefined
      }
    />
  );
}

function PickupBundleEditor({
  tierId,
  effectiveCurve,
  source,
  isLoading,
  isError,
  onRetry,
}: {
  tierId: string | null;
  effectiveCurve: PickupBundleCurve;
  source: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [second, setSecond] = useState("50");
  const [third, setThird] = useState("75");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const update = useUpdateCarrierPickupBundleDiscount();
  const submission = useSubmissionGuard();
  const pending = submission.pending || update.isPending;

  function openEditor() {
    setSecond(String(curvePercentageAt(effectiveCurve, 1)));
    setThird(String(curvePercentageAt(effectiveCurve, 2)));
    setValidationError(null);
    setServerError(null);
    setOpen(true);
  }

  async function submit() {
    const a = Number(second);
    const b = Number(third);
    if (
      ![a, b].every(
        (value) => Number.isFinite(value) && value >= 0 && value <= 100
      )
    ) {
      setValidationError("Bundle discounts must be between 0 and 100.");
      return;
    }
    await submission.run(async () => {
      setValidationError(null);
      setServerError(null);
      try {
        await update.mutateAsync({
          ...(tierId ? { carrierCustomerTierId: tierId } : {}),
          tiers: [
            { position: 0, fractionOff: 0 },
            { position: 1, fractionOff: a / 100 },
            { position: 2, fractionOff: b / 100 },
          ],
        });
        toast.success("Pickup bundle saved.");
        setOpen(false);
      } catch (error) {
        const message = mutationFailure(error, "save this pickup bundle");
        setServerError(message);
        toast.error(message);
      }
    });
  }

  return (
    <>
      <Button
        type="button"
        aria-label="Edit pickup bundle"
        variant="ghost"
        size="sm-touch"
        onClick={openEditor}
        disabled={isLoading || isError}
      >
        Edit
      </Button>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!pending) setOpen(nextOpen);
        }}
        title="Pickup bundle"
        presentation="responsive-sheet"
      >
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            First package is always full price. Each extra package on the same
            pickup gets this much off its pickup fee (0–100%).
          </p>
          <p className="text-xs font-medium text-muted-foreground">{source}</p>
          {isLoading && (
            <p role="status" className="text-sm text-muted-foreground">
              Loading current pickup bundle...
            </p>
          )}
          {isError && (
            <div
              role="alert"
              className="flex items-center gap-2 text-sm text-destructive"
            >
              <span>Current pickup bundle is unavailable.</span>
              <Button
                type="button"
                variant="outline"
                size="sm-touch"
                onClick={onRetry}
              >
                Retry
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium" htmlFor="bundle-second">
              2nd package
              <Input
                id="bundle-second"
                aria-label="2nd package discount"
                className="mt-1"
                inputMode="decimal"
                value={second}
                onChange={(event) => setSecond(event.target.value)}
                disabled={pending}
              />
            </label>
            <label className="text-sm font-medium" htmlFor="bundle-third">
              3rd+ package
              <Input
                id="bundle-third"
                aria-label="3rd+ package discount"
                className="mt-1"
                inputMode="decimal"
                value={third}
                onChange={(event) => setThird(event.target.value)}
                disabled={pending}
              />
            </label>
          </div>
          {(validationError || serverError) && (
            <p role="alert" className="text-sm text-destructive">
              {validationError ?? serverError}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              size="touch"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              onClick={() => void submit()}
              disabled={pending || isLoading || isError}
            >
              {pending ? "Saving..." : "Save bundle"}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
